import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import { AddStaffForm } from "../../types/staff";
import { toast } from "sonner";
import StaffApis from "../../api/staffApis";
import {
  formatRoleForDisplay,
  removeNonNumeric,
} from "../../util/functions/UniversalFunctions";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function AddStaff() {
  const [formData, setFormData] = useState<AddStaffForm>({} as AddStaffForm);
  const [customDesignation, setCustomDesignation] = useState("");
  const [roles, setRoles] = useState<string[]>([]);

  const branchCode = useBranchCode();

  const [confirmPassword, setConfirmPassword] = useState("");

  const validateForm =
    formData.password === confirmPassword &&
    formData.fullName &&
    formData.email &&
    formData.qualification &&
    formData.mobile &&
    formData.dateOfJoining &&
    formData.roleName;

  const formatRoleName = (role?: string) => {
    if (!role) return undefined;

    if (role.startsWith("ROLE_")) {
      return role;
    }

    return `ROLE_${role.trim().toUpperCase().replace(/\s+/g, "_")}`;
  };

  useEffect(() => {
    const fetchRoles = async () => {
      const response = await StaffApis.getStaffRoles();
      if (response?.data) {
        setRoles(response.data);
      }
    };

    fetchRoles();
  }, []);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = event.target;
    if (name === "mobile" || name === "alternatePhoneNumber") {
      const numericValue = removeNonNumeric(value).slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      return;
    }
    const newValue = type === "radio" ? value : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const resolvedRole =
      formData.roleName === "ROLE_OTHERS" ? customDesignation : formData.roleName;

    const finalData: AddStaffForm = {
      ...formData,
      roleName: formatRoleName(resolvedRole),
      gender: formData.gender?.toUpperCase() as "MALE" | "FEMALE",
      active: true,
      branchCode,
    };

    try {
      const response = await StaffApis.addStaff(finalData);
      if (response) {
        toast.success("Staff added successfully!");

        setFormData({} as AddStaffForm);
        setCustomDesignation("");
        setConfirmPassword("");
      }
    } catch (err) {
      toast.error("Failed to add staff");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between">
        <PageHeader showBack />
      </div>

      <form
        className="grid gap-7 bg-white px-6 py-5 rounded-lg"
        onSubmit={handleSubmit}
      >
        <div className="grid md:grid-cols-2 gap-7">
          <CustomInput
            name="fullName"
            label="Staff Name"
            type="text"
            placeholder="Enter staff name"
            value={formData.fullName || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
            required
          />

          <CustomSelect
            name="roleName"
            label="Designation"
            value={formData.roleName || ""}
            options={roles.map((role) => ({
              label: formatRoleForDisplay(role),
              value: role,
            }))}
            placeholder="Select staff's designation"
            onChange={handleInputChange}
            required
          />

          {formData.roleName === "ROLE_OTHERS" && (
            <CustomInput
              name="customDesignation"
              label="Custom Designation"
              type="text"
              placeholder="Enter custom designation"
              value={customDesignation}
              onChange={(e) => setCustomDesignation(e.target.value)}
              inputClassName="w-full"
            />
          )}

          <CustomInput
            name="employeeCode"
            label="Employee Code (Leave empty for autogeneration)"
            type="text"
            readOnly
            placeholder="Enter employee code"
            value={formData.employeeCode || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          <CustomInput
            name="email"
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            value={formData.email || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
            required
          />

          <CustomInput
            name="password"
            label="Password"
            type="password"
            placeholder="Enter Password"
            value={formData.password || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
            required
          />

          <div className="flex flex-col gap-1">
            <CustomInput
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              inputClassName="w-full"
              required
            />

            {formData.password !== confirmPassword && confirmPassword && (
              <p className="text-error-600 text-xs font-medium ml-1">
                * Passwords do not match
              </p>
            )}
          </div>

          <CustomInput
            name="mobile"
            label="Phone Number"
            type="tel"
            placeholder="Enter phone number"
            value={formData.mobile || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
            required
          />

          <CustomInput
            name="alternatePhoneNumber"
            label="Alt. Phone Number"
            type="tel"
            placeholder="Enter alternate phone number"
            value={formData.alternatePhoneNumber || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          <CustomInput
            name="dateOfBirth"
            label="Date of Birth"
            type="date"
            placeholder="DD/MM/YYYY"
            value={formData.dateOfBirth || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />

          <CustomInput
            name="dateOfJoining"
            label="Date of Joining"
            type="date"
            placeholder="DD/MM/YYYY"
            value={formData.dateOfJoining || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
            required
          />
        </div>

        <div className="grid gap-7">
          {/* Gender */}
          <div className="flex gap-7 items-center">
            <label className="text-sm font-semibold text-neutral-400 block">
              Gender
            </label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CustomInput
                  name="gender"
                  type="radio"
                  value="MALE"
                  checked={formData.gender === "MALE"}
                  onChange={handleInputChange}
                  inputClassName="w-3 h-3"
                />
                <span
                  className={`text-sm font-semibold ${
                    formData.gender === "MALE"
                      ? "text-primary-500"
                      : "text-neutral-300"
                  }`}
                >
                  Male
                </span>
              </div>

              <div className="flex items-center gap-2">
                <CustomInput
                  name="gender"
                  type="radio"
                  value="FEMALE"
                  checked={formData.gender === "FEMALE"}
                  onChange={handleInputChange}
                  inputClassName="w-3 h-3"
                />
                <span
                  className={`text-sm font-semibold ${
                    formData.gender === "FEMALE"
                      ? "text-primary-500"
                      : "text-neutral-300"
                  }`}
                >
                  Female
                </span>
              </div>
            </div>
          </div>

          <CustomInput
            name="qualification"
            label="Qualification"
            type="text"
            placeholder="Enter qualification"
            value={formData.qualification || ""}
            onChange={handleInputChange}
            inputClassName="w-full md:w-[70%]"
            required
          />

          <CustomInput
            name="about"
            label="About"
            type="text"
            isTextarea
            placeholder="Additional information about the staff..."
            value={formData.about || ""}
            onChange={handleInputChange}
            inputClassName="w-full"
          />
        </div>

        <div className="flex justify-end items-end">
          <CustomButton
            color="bg-primary-500"
            text="Submit"
            type="submit"
            disabled={!validateForm}
          />
        </div>
      </form>
    </div>
  );
}
